import React, { useEffect, useState } from 'react';
import { useDataProvider, useListContext, useNotify } from 'react-admin';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { DateTime } from 'luxon';

import { PermissionsProvider } from 'providers/permissionsProvider';
import { Show } from 'components/Show';
import InfoDialog from 'components/InfoDialog';
import { MarkAsPaidConfirm } from 'components/SelectionToolkit/MarkAsPaidConfirm';
import {
  MarkAsPaidTarget,
  useMarkAsPaidPrompt,
} from 'components/SelectionToolkit/useMarkAsPaidPrompt';
import { UserPublicInterface } from '@vatos-pas/common/dist/modules/user/interfaces/user-public.interface';
import { formatCurrency } from 'utils/currency';

/** One Payment Status History entry as returned by a *-paid resource. */
export type PaidHistoryEntry = {
  id: string;
  paid: boolean;
  createdAt?: Date | string | null;
  user?: Pick<UserPublicInterface, 'firstName' | 'lastName'> | null;
  /**
   * Amount the row was marked as paid with (Prod Payables MS entries written
   * since the Total Cost column); absent or null on Unpaid entries, on
   * entries written before that and on the Bumpout Timesheet.
   */
  amount?: number | string | null;
  /** Where the amount came from: 'TotalCost' or 'Calculated'. */
  amountSource?: string | null;
};

/** Labels of the API's JobPhasePaidAmountSourceEnum values. */
export const AMOUNT_SOURCE_LABELS: { [source: string]: string } = {
  TotalCost: 'Cost Override',
  Calculated: 'Calculated',
};

export type PaidStatusFieldProps<Body> = {
  /** Row id, written as the foreign key of every history entry. */
  id: string;
  paid: boolean;
  /** Shows the history icon. */
  hasHistory: boolean;
  /** Resource holding the paid history entries (e.g. `/job-phase-bump-paid`). */
  historyResource: string;
  /** Name of the entry field pointing at the row (e.g. `jobPhaseBumpId`). */
  foreignKeyName: string;
  /** Permission flag that enables the checkbox. */
  editPermission: string;
  title: string;
  /** Optional line rendered beneath the title, naming the row. */
  subtitle?: string;
  /** Optional bold heading rendered in the modal body above the entries. */
  bodyHeading?: string;
  /** Status text of an unpaid entry. */
  unpaidLabel?: string;
  /** The sheet's Mark as Paid batch and the body selecting just this row. */
  markAsPaid: MarkAsPaidTarget<Body> & { body: Body };
};

/** The sheets expose the history count as a string or a number. */
export const hasPaidHistory = (
  count: number | string | null | undefined,
): boolean => Number(count ?? 0) > 0;

/**
 * Paid? checkbox with its Payment Status History modal, shared by the Bumpout
 * Timesheet and the Prod Payables MS. Checking the box first asks the Mark as
 * Paid confirmation for the row; unchecking does not ask. The write is
 * optimistic: the box flips at once and reverts if the history write fails.
 */
export const PaidStatusField = <Body,>({
  id,
  paid,
  hasHistory,
  historyResource,
  foreignKeyName,
  editPermission,
  title,
  subtitle,
  bodyHeading,
  unpaidLabel = 'Unpaid',
  markAsPaid,
}: PaidStatusFieldProps<Body>) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { refetch, loading } = useListContext();
  const { hasPermission } = PermissionsProvider.useContainer();
  const classes = useStyles();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paidHistory, setPaidHistory] = useState<PaidHistoryEntry[]>([]);
  const [status, setStatus] = useState(paid);

  // React-admin renders the stale row first and revalidates it, so follow the
  // server value whenever it changes. Resyncing when a list load finishes also
  // discards an optimistic flip the server did not keep, even when the row
  // comes back with the same value it had before the click.
  useEffect(() => {
    if (!loading) setStatus(paid);
  }, [paid, loading]);

  const canEdit = hasPermission(editPermission);

  const savePaid = async (nextPaid: boolean) => {
    setStatus(nextPaid);
    try {
      await dataProvider.create(historyResource, {
        data: { [foreignKeyName]: id, paid: nextPaid },
      });
      notify('Changes were made successfully!');
      refetch();
    } catch (error) {
      notify(error.message, 'warning');
      setStatus(!nextPaid);
    }
  };

  const prompt = useMarkAsPaidPrompt(markAsPaid, () => {
    prompt.close();
    savePaid(true);
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      prompt.open(() => markAsPaid.body);
    } else {
      savePaid(false);
    }
  };

  const openModal = async () => {
    const response = await dataProvider.getList<PaidHistoryEntry>(
      historyResource,
      {
        filter: { [foreignKeyName]: id },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' },
      },
    );
    if (response.data) {
      setPaidHistory(response.data);
    }
    setIsModalOpen(true);
  };

  return (
    <Box display="flex" minWidth={80}>
      <Checkbox
        disabled={!canEdit || prompt.isLoading}
        checked={status}
        onChange={handleChange}
      />
      <Show condition={hasHistory}>
        <IconButton onClick={openModal} aria-label="Payment Status History">
          <InfoOutlinedIcon className={classes.infoWarning} />
        </IconButton>
      </Show>
      <MarkAsPaidConfirm {...prompt.dialogProps} />
      <InfoDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title={title}
        closeText="CLOSE"
        content={
          <>
            {subtitle && (
              <Box mb={2}>
                <Typography className={classes.text}>{subtitle}</Typography>
              </Box>
            )}
            {bodyHeading && (
              <Box mb={2}>
                <Typography className={classes.heading}>
                  {bodyHeading}
                </Typography>
              </Box>
            )}
            <Box>
              {paidHistory.map((item, index) => (
                <Box
                  key={item.id}
                  pb={1}
                  mb={index !== paidHistory.length - 1 ? 1 : 0}
                >
                  <Typography className={classes.text}>
                    {formatHistoryEntry(item)}
                  </Typography>
                  <Typography className={classes.text}>
                    Status: {item.paid ? 'Paid' : unpaidLabel}
                  </Typography>
                  {formatHistoryAmount(item) && (
                    <Typography className={classes.text}>
                      {formatHistoryAmount(item)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </>
        }
      />
    </Box>
  );
};

/** "First Last - h:mm a  MM/dd/yyyy" */
export const formatHistoryEntry = (item: PaidHistoryEntry): string => {
  const name = [item.user?.firstName, item.user?.lastName]
    .filter(Boolean)
    .join(' ');
  const createdAt = toDateTime(item.createdAt);
  const dateFormatted = createdAt ? createdAt.toFormat('t  MM/dd/yyyy') : '';
  return `${name} - ${dateFormatted}`;
};

/**
 * "Amount: $1,250.00 (Cost Override)" for a Paid entry that recorded the amount
 * it was paid with; null for Unpaid entries and for entries without one
 * (written before the amount existed, or from a sheet that records none),
 * which keep their shape.
 */
export const formatHistoryAmount = (item: PaidHistoryEntry): string | null => {
  if (!item.paid) return null;
  if (item.amount === null || item.amount === undefined || item.amount === '') {
    return null;
  }
  const source = item.amountSource
    ? AMOUNT_SOURCE_LABELS[item.amountSource] ?? item.amountSource
    : null;
  return `Amount: ${formatCurrency(item.amount)}${
    source ? ` (${source})` : ''
  }`;
};

const toDateTime = (value: PaidHistoryEntry['createdAt']): DateTime | null => {
  if (!value) return null;
  return value instanceof Date
    ? DateTime.fromJSDate(value)
    : DateTime.fromISO(value);
};

const useStyles = makeStyles({
  infoWarning: {
    color: '#ff9800',
  },
  heading: {
    color: 'black',
    fontWeight: 'bold',
  },
  text: {
    color: 'black',
    fontWeight: 'normal',
  },
});

export default PaidStatusField;
